import { collection, doc, getDocFromServer, getDocs, onSnapshot, runTransaction, serverTimestamp, Timestamp } from "firebase/firestore";
import { db } from "../firebase/firebase-config";
import { getPublishedVerifiedAddresses } from "./publicVerifiedAddresses";
import { getVerifiedAddressKey } from "./verifiedAddressStorage";
import { isAllowedNtid } from "./allowedNtids";

const USAGE_COLLECTION = "publicAddressUsage";
const INITIAL_LOAD_TIMEOUT_MS = 30000;
const isRecord = value => Boolean(value) && typeof value === "object" && !Array.isArray(value);

export class SharedAddressUsageError extends Error {
  constructor(code, message, cause) {
    super(message);
    this.name = "SharedAddressUsageError";
    this.code = code;
    if (cause) this.cause = cause;
  }
}

const unavailable = cause => cause instanceof SharedAddressUsageError ? cause : new SharedAddressUsageError(
  "SHARED_ADDRESS_UNAVAILABLE", "The shared address list is unavailable. Check your connection and try again.", cause);
const invalidNtid = () => new SharedAddressUsageError("SHARED_ADDRESS_NTID_INVALID", "Enter a valid NTID using 3-32 letters or numbers (for example, lkn01477).");
const invalidHistory = cause => new SharedAddressUsageError("SHARED_ADDRESS_HISTORY_INVALID", "The shared address history could not be read. Please try again later.", cause);
const normalizeNtid = value => {
  if (typeof value !== "string" || !/^[a-z0-9]{3,32}$/.test(value.trim().toLowerCase())) throw invalidNtid();
  return value.trim().toLowerCase();
};
const filterZip = value => {
  if (value === undefined || value === null || value === "" || value === "all") return "";
  if (typeof value !== "string" || !/^\d{5}$/.test(value)) throw new SharedAddressUsageError("SHARED_ADDRESS_ZIP_INVALID", "Choose a valid ZIP code.");
  return value;
};
const safeAddress = row => {
  getVerifiedAddressKey(row);
  return { id: row.id, provider: row.provider, address: row.address.trim(), zipCode: row.zipCode,
    status: "eligible", referenceId: row.referenceId, checkedAt: row.checkedAt,
    ...(row.sourceUrl !== undefined ? { sourceUrl: row.sourceUrl } : {}), ...(row.resultUrl !== undefined ? { resultUrl: row.resultUrl } : {}) };
};

export const addressUsageId = row => encodeURIComponent(getVerifiedAddressKey(row));

const currentCatalog = () => {
  const byId = new Map();
  for (const raw of getPublishedVerifiedAddresses()) {
    const address = safeAddress(raw);
    const id = addressUsageId(address);
    const previous = byId.get(id);
    if (!previous || address.checkedAt > previous.checkedAt
      || (address.checkedAt === previous.checkedAt && address.id.localeCompare(previous.id) < 0)) byId.set(id, address);
  }
  return [...byId.entries()].sort(([left], [right]) => left.localeCompare(right));
};
const timestampIso = value => {
  if (!value || typeof value.toDate !== "function") throw invalidHistory();
  const date = value.toDate();
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) throw invalidHistory();
  return date.toISOString();
};
const parseUsage = snapshot => {
  try {
    const value = snapshot.data();
    if (!isRecord(value) || typeof value.legacy !== "boolean" || typeof value.ntid !== "string"
      || (value.ntid !== "" && normalizeNtid(value.ntid) !== value.ntid)
      || (!value.legacy && !value.ntid)) throw invalidHistory();
    const address = safeAddress(value.address);
    if (addressUsageId(address) !== snapshot.id) throw invalidHistory();
    const usedAt = value.usedAt === null ? null : timestampIso(value.usedAt);
    const recordedAt = timestampIso(value.recordedAt);
    if ((usedAt === null && (!value.legacy || value.ntid !== "")) || (usedAt && usedAt > recordedAt)) throw invalidHistory();
    return { address, ntid: value.ntid, usedAt, recordedAt, legacy: value.legacy };
  } catch (error) { throw invalidHistory(error); }
};

export const subscribeSharedAddressUsage = (onChange, onError) => {
  let stopped = false;
  const reportError = error => { if (!stopped && typeof onError === "function") onError(unavailable(error)); };
  const timeout = setTimeout(() => reportError(unavailable()), INITIAL_LOAD_TIMEOUT_MS);
  let unsubscribe;
  try {
    unsubscribe = onSnapshot(collection(db, USAGE_COLLECTION), { includeMetadataChanges: true }, snapshot => {
      // A cached empty collection does not establish the shared queue's state.
      // Wait for server confirmation and committed server timestamps.
      if (stopped || snapshot.metadata?.fromCache || snapshot.metadata?.hasPendingWrites) return;
      clearTimeout(timeout);
      try { onChange(snapshot.docs.map(parseUsage)); } catch (error) { reportError(error); }
    }, error => { clearTimeout(timeout); reportError(error); });
  } catch (error) {
    clearTimeout(timeout);
    reportError(error);
  }
  return () => { stopped = true; clearTimeout(timeout); if (unsubscribe) unsubscribe(); };
};

export const claimNextSharedAddress = async ({ ntid, zipCode = "", excludedIds = [] } = {}) => {
  const identity = normalizeNtid(ntid);
  if (!isAllowedNtid(ntid)) throw new SharedAddressUsageError("SHARED_ADDRESS_NTID_NOT_ALLOWED", "This NTID is not approved. Enter an approved company NTID.");
  const zip = filterZip(zipCode);
  if (!Array.isArray(excludedIds) || excludedIds.some(id => typeof id !== "string")) {
    throw new SharedAddressUsageError("SHARED_ADDRESS_EXCLUSIONS_INVALID", "The address selection could not be prepared. Refresh and try again.");
  }
  try {
    const catalog = currentCatalog().filter(([, address]) => !zip || address.zipCode === zip);
    const snapshot = await getDocs(collection(db, USAGE_COLLECTION));
    if (snapshot.metadata?.fromCache) throw unavailable();
    // Validate every row before using this collection as the shared claim index.
    snapshot.docs.forEach(parseUsage);
    const used = new Set([...snapshot.docs.map(item => item.id), ...excludedIds]);
    const candidates = catalog.filter(([id]) => !used.has(id));
    for (let index = 0; index < candidates.length; index += 1) {
      const [id, address] = candidates[index];
      const ref = doc(db, USAGE_COLLECTION, id);
      const created = await runTransaction(db, async transaction => {
        const existing = await transaction.get(ref);
        if (existing.exists()) return false;
        transaction.set(ref, { address, ntid: identity, usedAt: serverTimestamp(), recordedAt: serverTimestamp(), legacy: false });
        return true;
      });
      if (!created) continue;
      // Only return the address after a committed server read supplies its times.
      const committed = await getDocFromServer(ref);
      if (!committed.exists()) throw unavailable();
      const usage = parseUsage(committed);
      return { ...usage, remaining: candidates.length - index - 1, total: catalog.length };
    }
    return null;
  } catch (error) { throw unavailable(error); }
};

export const migrateLocalAddressUsage = async rows => {
  if (!Array.isArray(rows)) throw invalidHistory();
  try {
    const catalog = new Map(currentCatalog());
    const incoming = new Map();
    let skipped = 0;
    for (const usage of rows) {
      if (!isRecord(usage)) throw invalidHistory();
      const id = addressUsageId(usage.address);
      // An old verification absent from the approved catalog stays local.
      if (!catalog.has(id) || incoming.has(id)) { skipped += 1; continue; }
      const ntid = usage.ntid === "" ? "" : normalizeNtid(usage.ntid);
      let usedAt = null;
      if (usage.usedAt !== null) {
        if (typeof usage.usedAt !== "string" || !Number.isFinite(Date.parse(usage.usedAt))
          || new Date(usage.usedAt).toISOString() !== usage.usedAt || Date.parse(usage.usedAt) > Date.now()) throw invalidHistory();
        usedAt = Timestamp.fromDate(new Date(usage.usedAt));
      } else if (ntid !== "") throw invalidHistory();
      // Historical records remain readable, but only approved identities may
      // create new shared usage, including imports from older browser queues.
      if (!isAllowedNtid(usage.ntid)) { skipped += 1; continue; }
      incoming.set(id, { address: catalog.get(id), ntid, usedAt, legacy: true });
    }
    let migrated = 0;
    for (const [id, usage] of incoming) {
      const ref = doc(db, USAGE_COLLECTION, id);
      const created = await runTransaction(db, async transaction => {
        if ((await transaction.get(ref)).exists()) return false;
        transaction.set(ref, { ...usage, recordedAt: serverTimestamp() });
        return true;
      });
      if (created) migrated += 1;
      else skipped += 1;
    }
    return { migrated, skipped };
  } catch (error) { throw unavailable(error); }
};
